import React from 'react';
import { useUserCanPerformAction, UserPermissionScope } from '@/utils/hooks/useUserCanPerformAction';
import Button from 'antd/lib/button';
import { AppPlatform, AppStoreType, CommonNotification, StepNotificationConfigOverride } from '../../types';
import { antGrey, runwayColors, rwColors } from '../styles/globalStyles';
import Space from 'antd/lib/space';
import Icon, { NumberOutlined, PlusCircleOutlined, SettingOutlined, TeamOutlined } from '@ant-design/icons';
import { displayTextForReleaseStepType, iconForAppNotificationId } from '../../helpers/displayHelpers';
import Text from 'antd/lib/typography/Text';
import Switch from 'antd/lib/switch';
import Divider from 'antd/lib/divider';
import Tooltip from 'antd/lib/tooltip';
import { DISALLOWED_ACTION_ERROR_MESSAGE } from '../HOCs/PermissionedActionButton';
import { RunwayUserAction } from '../../helpers/aclHelpers';
import { StepGraphStep } from '@/types/workflow';
import { Destination } from '@/types/destinations';
import { DestinationCounter } from '../appHome/DestinationCounter';

type SwitchState = 'on' | 'off' | 'indeterminate';

interface NotificationRowProps {
    notification: CommonNotification;
    notificationsEnabled: boolean;
    switchState?: SwitchState;
    isUpdating?: boolean;
    onClickSettings: (notificationId: string) => void;
    onToggleEnabled?: (enabled: boolean) => void;
    onAddOverride?: (notificationId: string) => void;
    actionType: RunwayUserAction;
    bucketId?: string;
    permissionScope?: UserPermissionScope;
    // Optional context used to render rich tooltips (Channel/@mention with per-step overrides).
    stepGraphStepsMap?: Record<string, StepGraphStep> | null;
    destinationsByStepConfigId?: Record<string, Destination[]>;
    platform?: AppPlatform;
    appStoreType?: AppStoreType;
    availableChannelsById?: Record<string, string>;
    primaryChannelNames?: string[];
}

interface ValueIndicators {
    hasChannel: boolean;
    hasMention: boolean;
}

// An icon shows whenever that value is configured somewhere — either at the parent
// (notification default) or in any of the step-config overrides.
const computeValueIndicators = (
    notification: CommonNotification,
    overrides: StepNotificationConfigOverride[],
): ValueIndicators => {
    const parentHasChannel = (notification.channels?.length ?? 0) > 0;
    const parentHasMention =
        (notification.mentionGroups?.length ?? 0) > 0 || (notification.mentionUsers?.length ?? 0) > 0;
    const anyOverrideHasChannel = overrides.some((o) => (o.channels?.length ?? 0) > 0);
    const anyOverrideHasMention = overrides.some(
        (o) => (o.settings?.mentionGroups?.length ?? 0) > 0 || (o.settings?.mentionUsers?.length ?? 0) > 0,
    );

    return {
        hasChannel: parentHasChannel || anyOverrideHasChannel,
        hasMention: parentHasMention || anyOverrideHasMention,
    };
};

const ICON_BUTTON_PADDING = 8;

const TriStateSwitch: React.FC<{
    state: SwitchState;
    disabled?: boolean;
    onChange?: (nextOn: boolean) => void;
}> = ({ state, disabled, onChange }) => {
    const handleClick = (e: React.MouseEvent | React.KeyboardEvent) => {
        e.stopPropagation();
        if (disabled || !onChange) return;
        // Indeterminate or off → turn all on; on → turn all off
        onChange(state !== 'on');
    };
    if (state === 'indeterminate') {
        return (
            <span
                onClick={handleClick}
                style={{
                    width: 44,
                    height: 22,
                    backgroundColor: rwColors.primary,
                    borderRadius: 11,
                    cursor: disabled ? 'not-allowed' : 'pointer',
                    opacity: disabled ? 0.4 : 1,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    transition: 'background-color 0.2s',
                }}
            >
                <span
                    style={{
                        width: 14,
                        height: 3,
                        backgroundColor: runwayColors.white,
                        borderRadius: 1.5,
                    }}
                />
            </span>
        );
    }
    return (
        <span style={{ display: 'flex', alignItems: 'center' }} onClick={(e) => e.stopPropagation()}>
            <Switch checked={state === 'on'} disabled={disabled} onChange={(_c, event) => handleClick(event)} />
        </span>
    );
};

const NotificationRow: React.FC<NotificationRowProps> = (props: NotificationRowProps) => {
    const {
        notification,
        actionType,
        bucketId,
        permissionScope,
        onToggleEnabled,
        onAddOverride,
        stepGraphStepsMap,
        destinationsByStepConfigId,
        platform,
        appStoreType,
        availableChannelsById,
        primaryChannelNames,
        isUpdating,
    } = props;
    const canAddOverride = (notification.applicableStepArchetypes?.length ?? 0) > 0;
    const notificationsEnabled = props.notificationsEnabled;
    const overrides = notification.stepConfigOverrides ?? [];

    const userCanToggle = useUserCanPerformAction(
        actionType,
        undefined,
        undefined,
        bucketId,
        undefined,
        undefined,
        permissionScope,
    );

    const formatChannel = (name: string) => (name.startsWith('#') ? name : `#${name}`);
    const resolveChannelName = (c: { id: string; name?: string }) => availableChannelsById?.[c.id] ?? c.name ?? c.id;

    const switchState: SwitchState = props.switchState ?? (notification.enabled ? 'on' : 'off');

    const { hasChannel, hasMention } = computeValueIndicators(notification, overrides);
    const hasAnyOverrideIcon = hasChannel || hasMention;

    const defaultChannelNames = (notification.channels ?? []).map((c) => formatChannel(resolveChannelName(c)));
    const defaultMentionList = [...(notification.mentionGroups ?? []), ...(notification.mentionUsers ?? [])];

    const sortedEqual = (a: string[], b: string[]) => {
        if (a.length !== b.length) return false;
        const sa = [...a].sort();
        const sb = [...b].sort();
        return sa.every((v, i) => v === sb[i]);
    };

    const parentChannelIds = (notification.channels ?? []).map((c) => c.id);
    const channelOverrides = overrides.filter((o) => {
        if ((o.channels?.length ?? 0) === 0) return false;
        const overrideIds = (o.channels ?? []).map((c) => c.id);
        return !sortedEqual(parentChannelIds, overrideIds);
    });

    const mentionOverrides = overrides.filter((o) => {
        const overrideMentions = [...(o.settings?.mentionGroups ?? []), ...(o.settings?.mentionUsers ?? [])];
        if (overrideMentions.length === 0) return false;
        return !sortedEqual(defaultMentionList, overrideMentions);
    });

    const tooltipLabelStyle: React.CSSProperties = { color: antGrey.grey5 };

    const renderOverrideCells = (override: StepNotificationConfigOverride, valueText: React.ReactNode) => {
        const step = stepGraphStepsMap?.[override.stepConfigId];
        const archetypeText =
            step && platform && appStoreType ?
                displayTextForReleaseStepType(step.archetype, platform, appStoreType, true)
            :   null;
        const destinations = destinationsByStepConfigId?.[override.stepConfigId] ?? [];
        return (
            <React.Fragment key={override.stepConfigId}>
                <span style={{ display: 'inline-flex', alignItems: 'center' }}>
                    {destinations.length > 0 && (
                        <DestinationCounter size={16} destinations={destinations} hideTooltip />
                    )}
                </span>
                <span style={{ whiteSpace: 'nowrap' }}>
                    {archetypeText && (
                        <>
                            <span style={tooltipLabelStyle}>{archetypeText}</span>
                            <span style={{ ...tooltipLabelStyle, margin: '0 6px' }}>|</span>
                        </>
                    )}
                    <span>{step?.stepName ?? override.stepConfigId}</span>
                </span>
                <span style={{ paddingLeft: 12, whiteSpace: 'nowrap' }}>{valueText}</span>
            </React.Fragment>
        );
    };

    const sectionDividerStyle: React.CSSProperties = {
        borderBottom: `1px solid ${antGrey.grey6}`,
        paddingBottom: 8,
    };
    const overridesLabel = (count: number) => (count === 1 ? 'Override:' : 'Overrides:');
    const overridesGridStyle: React.CSSProperties = {
        display: 'grid',
        gridTemplateColumns: 'auto auto auto',
        columnGap: 8,
        rowGap: 4,
        alignItems: 'center',
        marginTop: 4,
    };

    const channelHeaderText =
        defaultChannelNames.length > 0 ? defaultChannelNames.join(', ')
        : (primaryChannelNames?.length ?? 0) > 0 ?
            `${(primaryChannelNames ?? []).map((n) => formatChannel(n)).join(', ')} (default)`
        :   'None';
    const channelTooltip = (
        <div>
            <div style={channelOverrides.length > 0 ? sectionDividerStyle : undefined}>
                <span style={tooltipLabelStyle}>Channel:</span> {channelHeaderText}
            </div>
            {channelOverrides.length > 0 && (
                <>
                    <div style={{ ...tooltipLabelStyle, marginTop: 8 }}>{overridesLabel(channelOverrides.length)}</div>
                    <div style={overridesGridStyle}>
                        {channelOverrides.map((o) =>
                            renderOverrideCells(
                                o,
                                (o?.channels ?? []).map((c) => formatChannel(resolveChannelName(c))).join(', '),
                            ),
                        )}
                    </div>
                </>
            )}
        </div>
    );

    const mentionTooltip = (
        <div>
            <div style={mentionOverrides.length > 0 ? sectionDividerStyle : undefined}>
                <span style={tooltipLabelStyle}>@mention:</span>{' '}
                {defaultMentionList.length > 0 ? defaultMentionList.join(', ') : 'None'}
            </div>
            {mentionOverrides.length > 0 && (
                <>
                    <div style={{ ...tooltipLabelStyle, marginTop: 8 }}>{overridesLabel(mentionOverrides.length)}</div>
                    <div style={overridesGridStyle}>
                        {mentionOverrides.map((o) => {
                            const items = [...(o.settings?.mentionGroups ?? []), ...(o.settings?.mentionUsers ?? [])];
                            return renderOverrideCells(o, items.length > 0 ? items.join(', ') : '(cleared)');
                        })}
                    </div>
                </>
            )}
        </div>
    );

    return (
        <div
            style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                flexGrow: 1,
                paddingRight: 12,
            }}
        >
            <Space size={18} align="center">
                <Icon
                    component={iconForAppNotificationId(notification.id)}
                    style={{
                        fontSize: 16,
                        color: notificationsEnabled ? antGrey.grey7 : antGrey.grey5,
                        paddingTop: 4,
                    }}
                />
                <Text type={notificationsEnabled || notification.enabled ? undefined : 'secondary'}>
                    {notification.displayText}
                </Text>
            </Space>
            <Space size={14} align="center">
                {hasChannel && (
                    <Tooltip
                        title={channelTooltip}
                        placement="leftTop"
                        overlayStyle={{ maxWidth: 1000 }}
                        mouseEnterDelay={0}
                        mouseLeaveDelay={0}
                    >
                        <NumberOutlined style={{ color: antGrey.grey7, fontSize: 14 }} />
                    </Tooltip>
                )}
                {hasMention && (
                    <Tooltip
                        title={mentionTooltip}
                        placement="leftTop"
                        overlayStyle={{ maxWidth: 1000 }}
                        mouseEnterDelay={0}
                        mouseLeaveDelay={0}
                    >
                        <TeamOutlined style={{ color: antGrey.grey7, fontSize: 14 }} />
                    </Tooltip>
                )}
                <Divider
                    type="vertical"
                    style={{
                        margin: '0 8px',
                        height: 20,
                        borderColor: antGrey.grey6,
                        visibility: hasAnyOverrideIcon ? 'visible' : 'hidden',
                    }}
                />
                <Space size={24} align="center">
                    {userCanToggle ?
                        <TriStateSwitch
                            state={switchState}
                            disabled={!notificationsEnabled || !onToggleEnabled || isUpdating}
                            onChange={onToggleEnabled}
                        />
                    :   <Tooltip title={DISALLOWED_ACTION_ERROR_MESSAGE} placement="topRight">
                            <span>
                                <TriStateSwitch state={switchState} disabled />
                            </span>
                        </Tooltip>
                    }
                    <Tooltip title="Settings" mouseEnterDelay={0} mouseLeaveDelay={0}>
                        <span style={{ display: 'flex', alignItems: 'center', marginInline: -ICON_BUTTON_PADDING }}>
                            <Button
                                type="text"
                                disabled={!notificationsEnabled}
                                icon={
                                    <SettingOutlined
                                        style={{ color: notificationsEnabled ? antGrey.grey7 : antGrey.grey5 }}
                                    />
                                }
                                onClick={(e) => {
                                    e.stopPropagation();
                                    props.onClickSettings(notification.id);
                                }}
                            />
                        </span>
                    </Tooltip>
                    <Tooltip title={canAddOverride ? 'Add override' : ''} mouseEnterDelay={0} mouseLeaveDelay={0}>
                        <span style={{ display: 'flex', alignItems: 'center', marginInline: -ICON_BUTTON_PADDING }}>
                            <Button
                                type="text"
                                disabled={!canAddOverride || !notificationsEnabled || !onAddOverride}
                                icon={
                                    <PlusCircleOutlined
                                        style={{
                                            color:
                                                canAddOverride && notificationsEnabled ? antGrey.grey7 : antGrey.grey5,
                                        }}
                                    />
                                }
                                onClick={(e) => {
                                    e.stopPropagation();
                                    onAddOverride?.(notification.id);
                                }}
                            />
                        </span>
                    </Tooltip>
                </Space>
            </Space>
        </div>
    );
};

export default NotificationRow;
